import React, { useEffect } from 'react';
import { CheckCircle, Lightbulb, PrinterIcon, Wrench, Scissors, Package, Award } from 'lucide-react';

export const Production = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const productionSteps = [
    {
      id: 1,
      icon: Lightbulb,
      title: "Tasarım",
      description: "Modern ve işlevsel ambalaj tasarımları ile markanızı öne çıkarıyoruz. Tasarım süreçlerimizde CAD programları ile bıçak çizimleri oluşturulmakta, grafik dosyaları Illustrator, Photoshop ve Esko gibi profesyonel yazılımlarla hazırlanmaktadır. Dijital prova ve plotter makinelerimiz ile tasarımlarınızı hızlıca numune haline getirerek onay sürecini hızlandırıyoruz."
    },
    {
      id: 2,
      icon: PrinterIcon,
      title: "Baskı",
      description: "Son teknoloji ofset baskı makinelerimizle yüksek kaliteli ve canlı baskılar elde ediyoruz. Avrupa menşeli KBA Rapida 164 (120x164 cm), Ultra Man ve Roland makinelerimiz ile büyük ebatlı kutuları bile tek seferde basabiliyoruz. Dispersiyon lak, soft touch lak, UV lak ve lokal lak uygulamaları ile kutularınıza premium bir görünüm kazandırıyoruz. Saatte yüksek tabaka kapasitesiyle hızlı ve hatasız üretim gerçekleştiriyoruz."
    },
    {
      id: 3,
      icon: Wrench,
      title: "Kağıt Sap Uygulaması",
      description: "Özellikle ağır ürünler için özel olarak geliştirdiğimiz kağıt sap entegrasyonu ile taşıma kolaylığı sağlıyoruz. %100 kağıttan üretilen dayanıklı saplarımız, 15 kg'a kadar yük taşıma kapasitesi sunar. Bu uygulama deterjan, temizlik, mama ve kedi kumu kutularında kullanıcı konforunu önemli ölçüde artırır."
    },
    {
      id: 4,
      icon: Scissors,
      title: "Kesim ve Katlama",
      description: "6 kesim ve 3 katlama makinesi ile yüksek üretim kapasitesi yakalıyoruz. Bobst ve benzeri hassas kesim makinelerimizde pilyaj, perforaj ve yırtma bandı uygulamalarını kusursuz şekilde gerçekleştiriyoruz. Tam otomatik hatlarımız sayesinde kesim artıkları verimli bir şekilde yönetilmektedir."
    },
    {
      id: 5,
      icon: Package,
      title: "Yapıştırma ve Bitirme",
      description: "Otomatik katlama-yapıştırma makinelerimiz ile kutulara son şeklini veriyoruz. Lazer kontrollü tutkal yoğunluğu ve barkod kontrol sistemleri ile kaliteyi her aşamada güvence altına alıyoruz. İsteğe bağlı kolay açılır bandı, iç bölücü ve özel fitment uygulamaları da yapabiliyoruz."
    },
    {
      id: 6,
      icon: Award,
      title: "Kalite ve Standartlar",
      description: "Tüm üretim sürecimiz uluslararası standartlara uygundur. Kullanılan tüm kağıtlar FDA onaylıdır. Üretimimiz BRC belgesiyle gıda güvenliği ve hijyen açısından en üst seviyede denetlenmektedir. Tamamen geri dönüştürülebilir %100 kağıt bazlı malzemelerle sürdürülebilir ve çevre dostu üretim yapıyoruz."
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-lime-600 to-lime-700">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-white mb-6">
              Üretim Süreçleri
            </h1>
            <p className="text-xl text-lime-100 mb-4">
              Yaratıcı ekibimiz ve son teknoloji üretim parkurumuz ile müşterilerimizin ihtiyaçlarına özel, yüksek mukavemetli ofset baskılı karton kutuları en kaliteli şekilde üretiyoruz.
            </p>
            <p className="text-lg text-lime-100">
              Deterjan & temizlik ürünleri, kedi-köpek maması, kedi kumu, kuru gıda ve otomotiv yedek parça kutularında 20 yılı aşkın deneyimimizle fark yaratıyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* Production Flow */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {productionSteps.map((step) => {
                const IconComponent = step.icon;
                return (
                  <div 
                    key={step.id}
                    className="bg-gradient-to-br from-lime-50 to-white p-8 rounded-2xl border-2 border-lime-100 hover:border-lime-400 hover:shadow-2xl transition-all duration-300 group"
                  >
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-gradient-to-br from-lime-600 to-lime-700 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                          <IconComponent className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-lime-600 transition-colors">
                          {step.title}
                        </h3>
                        <p className="text-gray-700 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Technology Highlight */}
      <section className="py-20 bg-gradient-to-br from-lime-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-lime-600 to-lime-700 rounded-3xl p-12 text-white text-center shadow-2xl">
              <h2 className="text-4xl font-bold mb-6">Teknolojik Altyapımız</h2>
              <div className="grid md:grid-cols-3 gap-8 mt-12">
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                  <div className="text-3xl font-bold mb-2">6+3</div>
                  <p className="text-lime-100">Kesim & Katlama Makinesi</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                  <div className="text-3xl font-bold mb-2">120×164 cm</div>
                  <p className="text-lime-100">KBA Rapida Baskı Boyutu</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                  <div className="text-3xl font-bold mb-2">15 kg</div>
                  <p className="text-lime-100">Kağıt Sap Taşıma Kapasitesi</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Certifications */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              Kalite Güvencemiz
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Yüksek Mukavemet • Üstün Baskı Kalitesi • Güvenli ve Sürdürülebilir Üretim
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-lime-50 to-white p-8 rounded-2xl border-2 border-lime-100 hover:shadow-xl transition-all">
                <CheckCircle className="w-12 h-12 text-lime-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">FDA Onaylı</h3>
                <p className="text-gray-600">Tüm kağıtlar FDA onaylıdır</p>
              </div>
              <div className="bg-gradient-to-br from-lime-50 to-white p-8 rounded-2xl border-2 border-lime-100 hover:shadow-xl transition-all">
                <CheckCircle className="w-12 h-12 text-lime-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">BRC Belgesi</h3>
                <p className="text-gray-600">Gıda güvenliği ve hijyen standardı</p>
              </div>
              <div className="bg-gradient-to-br from-lime-50 to-white p-8 rounded-2xl border-2 border-lime-100 hover:shadow-xl transition-all">
                <CheckCircle className="w-12 h-12 text-lime-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">%100 Geri Dönüşüm</h3>
                <p className="text-gray-600">Tamamen kağıt bazlı çevre dostu üretim</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
